import { useState } from "react";
import type { Column } from "@tanstack/react-table";
import { ArrowUp, ArrowDown, ArrowUpDown, ListFilter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const MAX_VISIBLE = 200;

interface DataColumnHeaderProps<TData> {
  column: Column<TData, unknown>;
  title: string;
}

/**
 * Generic column header with sort toggle and faceted checkbox filter popover.
 * Works with any TData type — companion to SelfContainedDataTable.
 */
export function DataColumnHeader<TData>({ column, title }: DataColumnHeaderProps<TData>) {
  const [facetSearch, setFacetSearch] = useState("");

  const sorted = column.getIsSorted();
  const isFiltered = column.getIsFiltered();
  const filterValue = column.getFilterValue();
  const selectedValues = (filterValue as string[] | undefined) ?? [];

  const SortIcon = sorted === "asc" ? ArrowUp : sorted === "desc" ? ArrowDown : ArrowUpDown;

  const allFacetedValues = Array.from(column.getFacetedUniqueValues().entries())
    .filter(([v]) => v != null && v !== "")
    .sort(([a], [b]) => String(a).localeCompare(String(b), "es"));

  const searchFiltered = facetSearch
    ? allFacetedValues.filter(([v]) =>
        String(v).toLowerCase().includes(facetSearch.toLowerCase())
      )
    : allFacetedValues;

  const isCapped = searchFiltered.length > MAX_VISIBLE;
  const facetedValues = isCapped ? searchFiltered.slice(0, MAX_VISIBLE) : searchFiltered;

  const toggleFacet = (value: string) => {
    const next = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    column.setFilterValue(next.length ? next : undefined);
  };

  return (
    <div className="flex items-center gap-0.5">
      {/* Sort */}
      <button
        className="flex items-center gap-1 rounded px-1 py-1 text-sm font-medium hover:bg-muted/60 focus-visible:outline-none"
        onClick={() => column.toggleSorting(sorted === "asc")}
      >
        {title}
        <SortIcon className={cn("size-3.5 shrink-0", sorted ? "opacity-100" : "opacity-35")} />
      </button>

      {/* Faceted filter popover */}
      <Popover onOpenChange={(open) => { if (!open) setFacetSearch(""); }}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 w-7 p-0",
              isFiltered ? "text-primary" : "text-muted-foreground hover:text-foreground"
            )}
            title={`Filtrar por ${title}`}
          >
            <ListFilter className="size-3.5" />
          </Button>
        </PopoverTrigger>

        <PopoverContent align="start" className="w-64 p-3">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground">
                Filtrar por {title}
              </p>
              {selectedValues.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => column.setFilterValue(undefined)}
                >
                  Limpiar
                </Button>
              )}
            </div>

            <Input
              placeholder="Buscar..."
              value={facetSearch}
              onChange={(e) => setFacetSearch(e.target.value)}
              className="h-7 text-xs"
            />

            <div className="max-h-52 space-y-0.5 overflow-y-auto pr-1">
              {facetedValues.length === 0 ? (
                <p className="py-3 text-center text-xs text-muted-foreground">
                  Sin opciones
                </p>
              ) : (
                facetedValues.map(([value, count]) => {
                  const strVal = String(value);
                  const checked = selectedValues.includes(strVal);
                  return (
                    <div
                      key={strVal}
                      className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 hover:bg-muted"
                      onClick={() => toggleFacet(strVal)}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleFacet(strVal)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="flex-1 truncate text-sm">{strVal}</span>
                      <span className="tabular-nums text-xs text-muted-foreground">
                        {count}
                      </span>
                    </div>
                  );
                })
              )}
            </div>

            {isCapped && (
              <p className="pt-1 text-center text-xs text-muted-foreground">
                Mostrando {MAX_VISIBLE} de {searchFiltered.length.toLocaleString()}.{" "}
                Escribe para filtrar.
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Active filter dot */}
      {isFiltered && (
        <span className="size-1.5 shrink-0 rounded-full bg-primary" />
      )}
    </div>
  );
}
